import axios from 'axios';
import { User } from '../models';

// Constants
const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID || '';
const REDIRECT_URI = process.env.REACT_APP_SPOTIFY_REDIRECT_URI || '';

// Only log in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Debug logger function
const debugLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[AuthService] ${message}`);
    if (data) console.log(data);
  }
};

// Log non-sensitive info in development only
if (isDevelopment) {
  debugLog('Environment mode:', process.env.NODE_ENV);
  debugLog('Auth service initialized');
}

const AUTH_ENDPOINT = 'https://accounts.spotify.com/authorize';
const TOKEN_ENDPOINT = 'https://accounts.spotify.com/api/token';
const SCOPES = [
  'user-read-private',
  'user-read-email',
  'playlist-read-private',
  'playlist-read-collaborative',
  'playlist-modify-public',
  'playlist-modify-private',
  'user-read-recently-played',
  'user-top-read',
  'user-library-read',
  'streaming',
  'user-read-playback-state',
  'user-modify-playback-state',
];

// Local storage keys
const ACCESS_TOKEN_KEY = 'spotify_access_token';
const REFRESH_TOKEN_KEY = 'spotify_refresh_token';
const EXPIRATION_TIME_KEY = 'spotify_token_expiration';
const CODE_VERIFIER_KEY = 'spotify_code_verifier';
const STATE_KEY = 'spotify_auth_state';
const AUTH_ATTEMPT_COUNT_KEY = 'spotify_auth_attempt_count';

// Helper functions for code verifier storage
const storeCodeVerifier = (verifier: string): void => {
  try {
    // Test storage availability before attempting to store
    const testKey = 'storage_test';
    const testValue = 'test';
    
    // Test localStorage
    let localStorageAvailable = false;
    try {
      localStorage.setItem(testKey, testValue);
      if (localStorage.getItem(testKey) === testValue) {
        localStorage.removeItem(testKey);
        localStorageAvailable = true;
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
    
    // Test sessionStorage
    let sessionStorageAvailable = false;
    try {
      sessionStorage.setItem(testKey, testValue);
      if (sessionStorage.getItem(testKey) === testValue) {
        sessionStorage.removeItem(testKey);
        sessionStorageAvailable = true;
      }
    } catch (e) {
      console.warn('sessionStorage not available:', e);
    }
    
    // Store in available storage mechanisms
    if (localStorageAvailable) {
      localStorage.setItem(CODE_VERIFIER_KEY, verifier);
      console.log('Code verifier stored in localStorage');
    }
    
    if (sessionStorageAvailable) {
      sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
      console.log('Code verifier stored in sessionStorage');
    }
    
    // Always try to store in cookies as a fallback
    // Use a longer expiration (24 hours) to ensure it's available after redirects
    document.cookie = `${CODE_VERIFIER_KEY}=${verifier};path=/;max-age=86400;SameSite=Lax`;
    console.log('Code verifier stored in cookies');
    
    // Track authentication attempts
    const attemptCount = localStorage.getItem(AUTH_ATTEMPT_COUNT_KEY);
    const newCount = attemptCount ? parseInt(attemptCount, 10) + 1 : 1;
    localStorage.setItem(AUTH_ATTEMPT_COUNT_KEY, newCount.toString());
    console.log(`Authentication attempt #${newCount}`);
    
    console.log('Code verifier stored in multiple locations');
  } catch (error) {
    console.error('Error storing code verifier:', error);
  }
};

// Function to encode the verifier for state parameter
const encodeVerifierForState = (verifier: string): string => {
  // Take first and last 8 characters of the verifier and combine them
  // This is enough to validate but not enough to reconstruct the full verifier
  const prefix = verifier.substring(0, 8);
  const suffix = verifier.substring(verifier.length - 8);
  return `${prefix}${suffix}`;
};

// Function to decode the verifier from state
const isStateMatchingVerifier = (state: string, verifier: string): boolean => {
  if (!state || !verifier) return false;
  
  const encodedVerifier = encodeVerifierForState(verifier);
  return state === encodedVerifier;
};

const getCodeVerifier = (state?: string): string | null => {
  try {
    console.log('Attempting to retrieve code verifier...');
    
    // Try all storage locations in parallel for better reliability
    const storageOptions = [
      { type: 'localStorage', value: localStorage.getItem(CODE_VERIFIER_KEY) },
      { type: 'sessionStorage', value: sessionStorage.getItem(CODE_VERIFIER_KEY) },
      { type: 'cookies', value: null }
    ];
    
    // Parse cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === CODE_VERIFIER_KEY) {
        storageOptions[2].value = value;
        break;
      }
    }
    
    // Log all found values (without revealing the actual verifier)
    storageOptions.forEach(option => {
      console.log(`Code verifier in ${option.type}: ${option.value ? 'Found' : 'Not found'}`);
    });
    
    // Find the first valid verifier
    for (const option of storageOptions) {
      if (option.value) {
        const verifier = option.value;
        
        // If state is provided, validate that the verifier matches the state
        if (state) {
          const isMatch = isStateMatchingVerifier(state, verifier);
          console.log(`Verifier from ${option.type} matches state: ${isMatch}`);
          
          if (!isMatch) {
            console.warn(`Verifier from ${option.type} doesn't match state, skipping`);
            continue;
          }
        }
        
        console.log(`Using code verifier from ${option.type}`);
        
        // Restore to all storage mechanisms for future use
        try {
          localStorage.setItem(CODE_VERIFIER_KEY, verifier);
          sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
          document.cookie = `${CODE_VERIFIER_KEY}=${verifier};path=/;max-age=86400;SameSite=Lax`;
        } catch (e) {
          console.warn('Error restoring verifier to all storage locations:', e);
        }
        
        return verifier;
      }
    }
    
    // If we have a state but no matching verifier, try to recover using the state
    if (state && state.length >= 16) {
      console.log('Attempting to recover verifier from state...');
      // This is a last resort and won't work for all cases, but might help in some scenarios
      // We're essentially creating a new verifier that has the same encoded form as the original
      const recoveredVerifier = state.substring(0, 8) + generateRandomString(48) + state.substring(8);
      console.log('Created recovery verifier from state');
      
      // Store this recovered verifier for future use
      try {
        localStorage.setItem(CODE_VERIFIER_KEY, recoveredVerifier);
        sessionStorage.setItem(CODE_VERIFIER_KEY, recoveredVerifier);
        document.cookie = `${CODE_VERIFIER_KEY}=${recoveredVerifier};path=/;max-age=86400;SameSite=Lax`;
      } catch (e) {
        console.warn('Error storing recovery verifier:', e);
      }
      
      return recoveredVerifier;
    }
    
    console.log('Code verifier not found in any storage');
    return null;
  } catch (error) {
    console.error('Error retrieving code verifier:', error);
    return null;
  }
};

const clearCodeVerifier = (): void => {
  try {
    localStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    localStorage.removeItem(STATE_KEY);
    document.cookie = `${CODE_VERIFIER_KEY}=;path=/;max-age=0`;
    console.log('Code verifier cleared from all storage');
  } catch (error) {
    console.error('Error clearing code verifier:', error);
  }
};

// Function to check if storage is working properly
const testStorage = (): boolean => {
  const testKey = 'storage_test';
  const testValue = 'test_' + Date.now();
  
  try {
    localStorage.setItem(testKey, testValue);
    const retrieved = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    
    if (retrieved === testValue) {
      console.log('Storage test passed');
      return true;
    } else {
      console.warn('Storage test failed: value mismatch');
      return false;
    }
  } catch (error) {
    console.error('Storage test failed with error:', error);
    return false;
  }
};

// Types
interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
}

// Helper functions
const generateRandomString = (length: number): string => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  let result = '';
  for (let i = 0; i < length; i++) {
    result += possible[values[i] % possible.length];
  }
  return result;
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return await crypto.subtle.digest('SHA-256', data);
};

const base64URLEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expirationTime: number | null = null;
  private currentUser: User | null = null;

  constructor() {
    this.loadFromStorage();
  }

  // Load tokens from local storage
  private loadFromStorage(): void {
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiration = localStorage.getItem(EXPIRATION_TIME_KEY);
    this.expirationTime = expiration ? parseInt(expiration, 10) : null;
  }

  // Save tokens to local storage
  private saveToStorage(tokens: TokenResponse): void {
    const expiresAt = Date.now() + tokens.expires_in * 1000;
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    localStorage.setItem(EXPIRATION_TIME_KEY, expiresAt.toString());
    
    this.accessToken = tokens.access_token;
    this.refreshToken = tokens.refresh_token;
    this.expirationTime = expiresAt;
  }

  // Check if the user is logged in and token is valid
  public isLoggedIn(): boolean {
    return !!this.accessToken && !!this.expirationTime && this.expirationTime > Date.now();
  }

  // Get the access token, refreshing if necessary
  public async getAccessToken(): Promise<string | null> {
    if (!this.accessToken) {
      return null;
    }

    // Only refresh if token is expired or about to expire (within 5 minutes)
    if (this.expirationTime) {
      const now = Date.now();
      const isExpired = this.expirationTime <= now;
      const isExpiringSoon = this.expirationTime - now < 300000; // 5 minutes
      
      if (isExpired || isExpiringSoon) {
        console.log('Token is expired or expiring soon, refreshing...');
        const success = await this.refreshAccessToken();
        if (!success) {
          return null;
        }
      }
    }

    return this.accessToken;
  }

  // Initiate the login process
  public async login(): Promise<void> {
    console.log('Login: Starting Spotify authentication process');
    
    // Test if storage is working properly
    const storageWorking = testStorage();
    console.log('Storage working properly:', storageWorking);
    
    // Generate code verifier and challenge
    const codeVerifier = generateRandomString(64);
    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64URLEncode(hashed);
    
    console.log('Login: Generated code verifier (length):', codeVerifier.length);
    
    // Generate state parameter that includes encoded verifier information
    const encodedVerifier = encodeVerifierForState(codeVerifier);
    const state = encodedVerifier;
    
    // Store the state for validation during callback
    try {
      localStorage.setItem(STATE_KEY, state);
      console.log('Login: State parameter stored');
    } catch (e) {
      console.warn('Login: Failed to store state parameter:', e);
    }
    
    // Store the code verifier in multiple locations
    storeCodeVerifier(codeVerifier);
    
    // Verify it was stored correctly
    const storedVerifier = getCodeVerifier();
    console.log('Login: Stored code verifier retrieved:', storedVerifier ? 'Yes' : 'No');
    
    // If storage test failed or verifier storage failed, show warning
    if (!storageWorking || !storedVerifier) {
      console.warn('Login: Storage issues detected, authentication may fail');
      // We'll continue anyway and rely on the state parameter as fallback
    }
    
    // Build the authorization URL with state parameter
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      scope: SCOPES.join(' '),
      state: state, // Include state parameter with encoded verifier
    });
    
    const authUrl = `${AUTH_ENDPOINT}?${params.toString()}`;
    console.log('Login: Redirecting to:', authUrl);
    
    // Redirect to Spotify authorization page
    window.location.href = authUrl;
  }

  // Handle the callback from Spotify
  public async handleCallback(code: string, state?: string): Promise<boolean> {
    console.log('Callback: Handling Spotify callback');
    console.log('Callback: Authorization code received (length):', code.length);
    console.log('Callback: State parameter received:', state ? 'Yes' : 'No');
    
    // Log storage state
    console.log('Callback: localStorage keys:', Object.keys(localStorage));
    console.log('Callback: sessionStorage keys:', Object.keys(sessionStorage));
    console.log('Callback: cookies present:', document.cookie ? 'Yes' : 'No');
    
    // Validate state parameter if present
    const storedState = localStorage.getItem(STATE_KEY);
    if (state && storedState && state !== storedState) {
      console.error('Callback: State mismatch, possible CSRF attack');
      return false;
    }
    
    // Try to get the code verifier with our helper function, passing state for recovery
    const codeVerifier = getCodeVerifier(state);
    console.log('Callback: Code verifier retrieved:', codeVerifier ? 'Yes' : 'No');
    
    if (!codeVerifier) {
      console.error('No code verifier found in any storage location');
      
      // Check if this is a retry attempt
      const attemptCount = localStorage.getItem(AUTH_ATTEMPT_COUNT_KEY);
      const currentCount = attemptCount ? parseInt(attemptCount, 10) : 0;
      
      if (currentCount <= 1) {
        console.log('First authentication attempt failed, will retry automatically');
        // Clear any existing data and increment attempt counter
        localStorage.setItem(AUTH_ATTEMPT_COUNT_KEY, '2');
        // Return false to trigger a retry
        return false;
      } else {
        console.error(`Authentication failed after ${currentCount} attempts`);
        // Reset attempt counter for next login flow
        localStorage.removeItem(AUTH_ATTEMPT_COUNT_KEY);
        return false;
      }
    }
    
    try {
      console.log('Handling Spotify callback with code:', code);
      console.log('Code verifier from localStorage:', codeVerifier);
      console.log('CLIENT_ID being used:', CLIENT_ID);
      console.log('REDIRECT_URI being used:', REDIRECT_URI);
      
      // Set up headers for the token request
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };
      
      // Note: We're using PKCE flow which doesn't require client secret
      console.log('Using PKCE flow without client secret');
      
      // Prepare the request body
      const requestBody = new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'authorization_code',
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier,
      });
      
      console.log('Token request body:', requestBody.toString());
      
      const response = await axios.post<TokenResponse>(
        TOKEN_ENDPOINT,
        requestBody,
        { 
          headers,
          validateStatus: () => true // Accept any status code to see the error
        }
      );
      
      console.log('Token response status:', response.status);
      console.log('Token response data:', response.data);
      
      if (response.status >= 400) {
        console.error('Token request failed:', response.data);
        throw new Error(`Token request failed with status ${response.status}: ${JSON.stringify(response.data)}`);
      }
      
      this.saveToStorage(response.data);
      clearCodeVerifier();
      
      // Fetch user profile
      await this.fetchUserProfile();
      
      return true;
    } catch (error: any) {
      console.error('Error exchanging code for token:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }
      return false;
    }
  }

  // Refresh the access token
  public async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      console.log('No refresh token available');
      return false;
    }
    
    try {
      console.log('Refreshing access token...');
      
      // Set up headers for the token refresh request
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };
      
      // Note: We're using PKCE flow which doesn't require client secret for refresh either
      console.log('Using PKCE flow without client secret for token refresh');
      
      const response = await axios.post<TokenResponse>(
        TOKEN_ENDPOINT,
        new URLSearchParams({
          client_id: CLIENT_ID,
          grant_type: 'refresh_token',
          refresh_token: this.refreshToken,
        }),
        { headers }
      );
      
      // Update tokens
      this.saveToStorage({
        ...response.data,
        refresh_token: response.data.refresh_token || this.refreshToken,
      });
      
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  // Fetch the user's profile
  public async fetchUserProfile(): Promise<User | null> {
    console.log('fetchUserProfile: Starting to fetch user profile');
    
    const token = await this.getAccessToken();
    console.log('fetchUserProfile: Token available:', !!token);
    
    if (!token) {
      console.log('fetchUserProfile: No token available, returning null');
      return null;
    }
    
    try {
      console.log('fetchUserProfile: Making API request to Spotify');
      
      const response = await axios.get('https://api.spotify.com/v1/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        validateStatus: () => true // Accept any status code to see the error
      });
      
      console.log('fetchUserProfile: Response status:', response.status);
      
      if (response.status >= 400) {
        console.error('fetchUserProfile: API request failed:', response.data);
        throw new Error(`API request failed with status ${response.status}: ${JSON.stringify(response.data)}`);
      }
      
      const data = response.data;
      console.log('fetchUserProfile: User data received:', JSON.stringify(data, null, 2));
      
      this.currentUser = {
        id: data.id,
        displayName: data.display_name,
        email: data.email,
        images: data.images || [],
        country: data.country,
        product: data.product,
        followers: data.followers?.total,
        uri: data.uri,
      };
      
      console.log('fetchUserProfile: User object created:', JSON.stringify(this.currentUser, null, 2));
      
      return this.currentUser;
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }
      return null;
    }
  }

  // Get the current user
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Logout
  public logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(EXPIRATION_TIME_KEY);
    clearCodeVerifier();
    
    this.accessToken = null;
    this.refreshToken = null;
    this.expirationTime = null;
    this.currentUser = null;
    
    console.log('User logged out, all tokens and verifiers cleared');
  }
}

// Create a singleton instance
export const authService = new AuthService();
